/* eslint-disable react-refresh/only-export-components -- this file holds the registry singleton and a thin lookup helper, neither of which are React components. */
import { TemplateType } from '@/types/boot';
import { CheckoutTemplate } from './CheckoutTemplate';
import { DefaultTemplate } from './DefaultTemplate';
import { SinglePaymentTemplate } from './SinglePaymentTemplate';
import { PreferredPaymentTemplate } from './PreferredPaymentTemplate';
import { EmbeddedViewTemplate } from './EmbeddedViewTemplate';

/**
 * Maps `TemplateType` discriminators to their concrete {@link CheckoutTemplate}
 * implementation. Consumers should always go through this registry instead
 * of importing variant classes directly.
 *
 * `get()` is total: an unknown / undefined type returns the DEFAULT template,
 * which preserves the historical "fall back to standard checkout when in
 * doubt" behaviour expected throughout the codebase.
 */
class TemplateRegistryImpl {
  private readonly templates = new Map<TemplateType, CheckoutTemplate>();
  private readonly fallback: CheckoutTemplate;

  constructor(defaultTemplate: CheckoutTemplate) {
    this.fallback = defaultTemplate;
    this.register(defaultTemplate);
  }

  register(template: CheckoutTemplate): this {
    this.templates.set(template.type, template);
    return this;
  }

  get(type: TemplateType | undefined): CheckoutTemplate {
    if (!type) return this.fallback;
    return this.templates.get(type) ?? this.fallback;
  }

  has(type: TemplateType): boolean {
    return this.templates.has(type);
  }

  all(): CheckoutTemplate[] {
    return [...this.templates.values()];
  }
}

/** App-wide singleton. New templates must be registered here exactly once. */
export const TemplateRegistry = new TemplateRegistryImpl(new DefaultTemplate())
  .register(new SinglePaymentTemplate())
  .register(new PreferredPaymentTemplate())
  .register(new EmbeddedViewTemplate());

/** Re-exported convenience for call-sites that just want the active template. */
export const getCheckoutTemplate = (type: TemplateType | undefined): CheckoutTemplate => TemplateRegistry.get(type);
